import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { nanoid } from 'nanoid'

import Input, { Label } from './input'
import styles from '../component-styles/add-by-email.module.scss'
import { validateEmail } from '../helpers/forms'
import {
  getUserAccountDetailsGQL,
  addUserToAccountGQL,
  setCurrentDropDown,
} from '../store/actions'
import { SiteState } from '../store/site-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import PermissionSelector from './permission-selector'
import Button from './button'
import { messages } from '../core/variables'
import { MultiSelectBox } from './select-box'
import { returnUnique, getItemByKeyValue } from '../helpers'
import { FormRow, LabelSlot, FieldSlot } from './row'
import ToolTip from './tooltip'

export default function AddByEmail() {
  const dispatch = useDispatch()
  const {
    site: { selected, loadingUserList, messages: siteMessages },
    graphql: { currentUser, currentAccount },
  } = useSelector(
    ({ site, graphql }: { site: SiteState; graphql: GraphQLState }) => ({
      site,
      graphql,
    }),
  )

  const { error: errorMessage, success: successMessage } = siteMessages
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string[]>([])
  const [success, setSuccess] = useState('')
  const [permission, setPermission] = useState<PermissionLevel>('regular')
  const [accountIDs, setAccountIDs] = useState<string[]>([])

  const handleSubmit = async () => {
    dispatch(setCurrentDropDown({ id: 'close' }))
    setError([])
    setSuccess('')

    if (email === '') {
      setError([messages.notValidEmail])
      return
    }

    const emails = email.replace(/ /g, '').split(/[;,]/gi)

    if (emails.length === 0) {
      setError([messages.notValidEmail])
      return
    }

    const alreadyExists = emails.filter((eml) => {
      if (!loadingUserList && selected && selected.pendingUserList) {
        const isPendingUser = getItemByKeyValue(
          selected.pendingUserList,
          'email',
          eml,
          false,
          true,
        )
        if (isPendingUser !== -1) {
          return true
        }
      }
      if (!loadingUserList && selected && selected.permissionList) {
        const isExistingUser = getItemByKeyValue(
          selected.permissionList,
          'email',
          eml,
          false,
          true,
        )
        if (isExistingUser !== -1) {
          return true
        }
      }
      return false
    })

    const useEmails = emails.filter((eml) => alreadyExists.indexOf(eml) === -1)

    if (alreadyExists.length > 0) {
      setError((msg) => {
        return [
          ...msg,
          `${alreadyExists.join(', ')} ${
            alreadyExists.length > 1 ? 'are already users' : 'is already a user'
          } on this account.`,
        ]
      })
      if (useEmails.length === 0) {
        return
      }
    }

    const { valid, invalid } = validateEmail(useEmails, true)
    if (invalid.length > 0) {
      setError((msg) => {
        return [...msg, messages.notValidEmail]
      })
      if (valid.length === 0) {
        return
      }
    }

    dispatch(
      addUserToAccountGQL({
        users: valid,
        accounts: accountIDs,
        permission,
      }),
    )
  }

  useEffect(() => {
    setSuccess(successMessage)
  }, [successMessage])

  useEffect(() => {
    dispatch(getUserAccountDetailsGQL())
  }, [])

  const useItems = useMemo(() => {
    if (
      currentUser &&
      Object.prototype.hasOwnProperty.call(currentUser, 'userAccountProfiles')
    ) {
      const { companyID } = currentAccount || { companyID: '' }
      const { userAccountProfiles } = currentUser

      const selectedAccounts = returnUnique(userAccountProfiles, 'accountID')
        .filter(
          (account) =>
            account.companyID === companyID &&
            (account.userPermission === 'admin' ||
              account.userPermission === 'support'),
        )
        .map((account) => {
          return {
            optionName: account.accountName,
            optionValue: account.accountID,
          }
        })
      setAccountIDs(selectedAccounts.map((item) => item.optionValue))
      return selectedAccounts
    }
    return []
  }, [currentUser])

  return (
    <div className={styles.subsection}>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <FormRow last className={styles.formRow}>
          <LabelSlot noPadding column>
            <Label id="addEmail" small>
              Email
            </Label>
            <ToolTip
              className={styles.tooltip}
              message="Add multiple users by separating email addresses with commas or semicolons."
            />
          </LabelSlot>
          <FieldSlot noPadding column>
            <Input
              name="addEmail"
              type="text"
              label="Email"
              value={email}
              onValueChange={(nextEmail: string) => setEmail(nextEmail)}
              style={{ maxWidth: 420 }}
              required
            />
          </FieldSlot>
        </FormRow>
        {!(useItems.length === 1) && (
          <FormRow last className={styles.formRow}>
            <LabelSlot noPadding column>
              <Label id="account-select" small>
                Accounts
              </Label>
              <ToolTip
                className={styles.tooltip}
                message="Select the accounts to add the user(s) to."
              />
            </LabelSlot>
            <FieldSlot noPadding column>
              <MultiSelectBox
                key={JSON.stringify(useItems)}
                loading={useItems.length === 0}
                id="account-select"
                className={styles.multiSelect}
                value={accountIDs}
                items={useItems}
                placeholder="Select accounts"
                allValue="All accounts"
                onChange={(val) => {
                  setAccountIDs(val)
                }}
              />
            </FieldSlot>
          </FormRow>
        )}
        <FormRow last className={styles.formRow}>
          <LabelSlot noPadding column>
            <Label id="permission" small>
              Permission
            </Label>
            <ToolTip
              className={styles.tooltip}
              message="**Regular** users can create and view campaign codes. **Admin** users can also edit your campaign code structure and account details."
            />
          </LabelSlot>
          <FieldSlot noPadding column>
            <PermissionSelector
              permission={permission}
              onChange={(nextPermission) => setPermission(nextPermission)}
            />
          </FieldSlot>
        </FormRow>
        <FormRow
          last
          className={classNames(styles.formRow, styles.formRowButton)}
        >
          <FieldSlot noPadding column>
            <Button
              disabled={email === '' || accountIDs.length === 0}
              submit
              type="container"
              className={styles.noMargin}
            >
              Add
            </Button>
          </FieldSlot>
        </FormRow>
      </form>
      <div className={styles.formMessages}>
        {success ? <p className={styles.success}>{success}</p> : null}
        {error.length > 0 &&
          error.map((err) => (
            <p className={styles.error} key={nanoid()}>
              {error}
            </p>
          ))}
        {errorMessage ? <p className={styles.error}>{errorMessage}</p> : null}
      </div>
    </div>
  )
}
